import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { waLink } from "@/data/content";
import { FadeUp, Overline } from "./Reveal";

const JOURNEY_OPTIONS = ["Tourist", "Relocator", "Diaspora Returnee", "Investor", "Something else"];

export const Concierge = () => {
  const [form, setForm] = useState({ name: "", email: "", journey: "Tourist", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      setSent(true);
      toast.success("Enquiry received — our concierge will be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-jawa-ivory/20 bg-jawa-nearblack/40 px-4 py-3.5 text-sm text-jawa-ivory placeholder:text-jawa-ivory/40 focus:border-jawa-gold focus:outline-none transition-colors";

  return (
    <section id="concierge" data-testid="concierge-section" className="bg-jawa-espresso">
      <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <div className="grid gap-16 lg:grid-cols-2">
          <FadeUp>
            <Overline dark>Your journey starts with a conversation</Overline>
            <h2 className="mt-4 font-serif text-4xl font-medium tracking-tight text-jawa-ivory sm:text-5xl">
              Talk to the JÀWÀ Concierge.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-jawa-ivory/70">
              One message is all it takes. Tell us where you are heading — or where you are
              coming home to — and we will take it from there.
            </p>
            <a
              href={waLink("Hello JÀWÀ Concierge — I'd like to start planning.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="concierge-whatsapp-button"
              className="mt-10 inline-flex items-center gap-3 bg-jawa-ivory px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-espresso transition-colors duration-300 hover:bg-jawa-gold"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp the Concierge
            </a>
            <p className="mt-5 text-xs uppercase tracking-[0.2em] text-jawa-ivory/40">
              Replies within one working day
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            {sent ? (
              <div
                data-testid="enquiry-success"
                className="flex h-full flex-col items-start justify-center border border-jawa-gold/40 bg-jawa-nearblack/40 p-10"
              >
                <p className="font-serif text-3xl text-jawa-gold">Thank you.</p>
                <p className="mt-4 max-w-sm text-sm leading-relaxed text-jawa-ivory/70">
                  Your enquiry has been received. A member of the JÀWÀ concierge team will
                  reply within one working day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="enquiry-form" className="border border-jawa-ivory/15 bg-jawa-nearblack/40 p-8">
                <p className="text-xs uppercase tracking-[0.3em] text-jawa-gold">Send an enquiry</p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <input
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Full name"
                    data-testid="enquiry-name-input"
                    className={inputCls}
                  />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="Email address"
                    data-testid="enquiry-email-input"
                    className={inputCls}
                  />
                </div>
                <select
                  value={form.journey}
                  onChange={update("journey")}
                  data-testid="enquiry-journey-select"
                  className={`${inputCls} mt-4`}
                >
                  {JOURNEY_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-jawa-espresso">
                      I am a… {o}
                    </option>
                  ))}
                </select>
                <textarea
                  required
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your plans…"
                  data-testid="enquiry-message-input"
                  className={`${inputCls} mt-4 resize-none`}
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="enquiry-submit-button"
                  className="mt-6 w-full bg-jawa-terracotta px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors duration-300 hover:bg-jawa-gold hover:text-jawa-espresso disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send Enquiry"}
                </button>
              </form>
            )}
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
